import React, { useState, useEffect, ChangeEvent } from "react";
import {
  deleteCall,
  getCall,
  postCall,
  putCall,
} from "../../services/apiService";
import { endPoints, fieldTypes, PORTAL } from "../../constants/urlConstants";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../common/components/Button";
import styles from "./index.module.scss";
import {
  getProgramImage,
  getProgramImageForBanner,
} from "../../utils/commonFunctions";
import deleteIcon from "../../assets/images/delete-icon.svg";
import { ArrowLeft } from "lucide-react";
import editbutton from "../../assets/images/editbutton.svg";
import CustomDropDown from "../../common/components/CustomDropDown";
import { ProgramQuestionMap } from "../../types/program";
import Stepper from "../programStepper";
import Select from "react-select";
import CustomDatePicker from "../../common/components/CustomDatePicker";

const API_ENDPOINTS = [
  {
    label: "RM List",
    value: "user?filters=%7B%22role%22%3A%20%226%22%7D",
    type: "api",
  },
];

interface Option {
  id: number;
  name: string;
  type: string;
  status: string;
}

interface QuestionOptionMap {
  id: number;
  option: Option;
}

interface CustomQuestion {
  question: {
    label: string;
    type: string;
    section: any;
    config: any;
    status: string;
    answerLocation: string;
    bindingKey: string;
  };
  options: {
    name: string;
    type: string;
    category: any;
  }[];
  displayOrder: number;
}

interface DependsOnConfig {
  questionId: number;
  value: string[];
}

interface QuestionConfig {
  isRequired: boolean;
  isAdvancedValidation: boolean;
  minCharacter: number | null;
  maxCharacters: number | null;
  validationPattern: string | null;
  dependsOn?: DependsOnConfig;
  placeholder?: string;
  // Add more validation fields
  minValue?: number | null;
  maxValue?: number | null;
  allowDecimals?: boolean;
  maxFileSize?: number | null;
  allowedFileTypes?: string | null;
  allowFutureYears?: boolean;
  allowPastYears?: boolean;
  minYear?: "current" | number | null;
  maxYear?: "current" | number | null;
  endPoint?: string | null;
  dateValidationType?: "none" | "static" | "dynamic";
  startDate?: string | null;
  endDate?: string | null;
  pastValidation?: {
    enabled: boolean;
    minValue: number | null;
    maxValue: number | null;
    unit: "days" | "months" | "years";
  };
  futureValidation?: {
    enabled: boolean;
    minValue: number | null;
    maxValue: number | null;
    unit: "days" | "months" | "years";
  };
  excludeWeekends?: boolean;
  onlyWeekdays?: boolean;
  allowMahatriaChoice?: boolean; // Add this new field
}

// Helper function to get default validation config based on field type
const getDefaultValidationConfig = (fieldType: string) => {
  const baseConfig = {
    isRequired: false,
    // isAdvancedValidation: false,
    placeholder: null,
    dependsOn: undefined,
  };

  switch (fieldType) {
    case "text":
      return {
        ...baseConfig,
        minCharacter: null,
        maxCharacters: null,
        validationPattern: null,
      };
    case "textarea":
      return {
        ...baseConfig,
        minCharacter: null,
        maxCharacters: null,
        validationPattern: null,
      };
    case "number":
      return {
        ...baseConfig,
        minValue: null,
        maxValue: null,
        allowDecimals: false,
        minCharacter: null,
        maxCharacters: null,
        validationPattern: null,
      };
    case "email":
      return {
        ...baseConfig,
        validationPattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+", // Default email pattern
        minCharacter: null,
        maxCharacters: null,
      };
    case "file":
      return {
        ...baseConfig,
        maxFileSize: 10,
        allowedFileTypes: null,
        validationPattern: null,
      };
    case "year":
      return {
        ...baseConfig,
        minYear: null,
        maxYear: "current",
        minCharacter: null,
        maxCharacters: null,
        validationPattern: null,
        minValue: null,
        maxValue: null,
        allowDecimals: false,
      };
    case "apicall":
      return {
        ...baseConfig,
        endPoint: "user?filters=%7B%22role%22%3A%20%226%22%7D",
        minCharacter: null,
        maxCharacters: null,
        validationPattern: null,
        minValue: null,
        maxValue: null,
        allowDecimals: false,
      };
    case "date":
      return {
        ...baseConfig,
        dateValidationType: "none",
        startDate: null,
        endDate: null,
        pastValidation: {
          enabled: false,
          minValue: null,
          maxValue: null,
          unit: "days",
        },
        futureValidation: {
          enabled: false,
          minValue: null,
          maxValue: null,
          unit: "days",
        },
        excludeWeekends: false,
        onlyWeekdays: false,
      };
    case "draganddrop":
      return {
        ...baseConfig,
        maxFileSize: 10,
        allowedFileTypes: null,
        validationPattern: null,
        allowMultiple: false,
        allowMahatriaChoice: false, // Add default value
      };
    default:
      return baseConfig;
  }
};

// Add this helper function near your other utility functions
const calculateDateLimits = (config: QuestionConfig) => {
  const today = new Date();
  let minDate = null;
  let maxDate = null;

  if (config.dateValidationType === "dynamic") {
    if (config.pastValidation?.enabled) {
      const { minValue, maxValue, unit } = config.pastValidation;

      if (maxValue) {
        minDate = new Date(today);
        if (unit === "days") minDate.setDate(today.getDate() - maxValue);
        if (unit === "months") minDate.setMonth(today.getMonth() - maxValue);
        if (unit === "years")
          minDate.setFullYear(today.getFullYear() - maxValue);
      }
    }

    if (config.futureValidation?.enabled) {
      const { minValue, maxValue, unit } = config.futureValidation;

      if (maxValue) {
        maxDate = new Date(today);
        if (unit === "days") maxDate.setDate(today.getDate() + maxValue);
        if (unit === "months") maxDate.setMonth(today.getMonth() + maxValue);
        if (unit === "years")
          maxDate.setFullYear(today.getFullYear() + maxValue);
      }
    }
  }

  return { minDate, maxDate };
};

const getAvailableRadioQuestions = (
  sections: any[],
  currentQuestionIndex: number,
  currentSectionIndex: number,
) => {
  const radioQuestions: Array<{ id: number; label: string; options: any[] }> =
    [];

  const currentSection = sections[currentSectionIndex];
  if (!currentSection) return radioQuestions;

  currentSection.questions.forEach((question: any, qIndex: number) => {
    if (qIndex < currentQuestionIndex && question.question.type === "radio") {
      radioQuestions.push({
        id: question.question.id,
        label: question.question.label,
        options: question.question.questionOptionMaps || [],
      });
    }
  });

  return radioQuestions;
};

const getRadioOptionsForQuestion = (sections: any[], questionId: number) => {
  let options: any[] = [];

  sections.forEach((section) => {
    section.questions.forEach((question: any) => {
      if (question.question.id === questionId) {
        options = question.question.questionOptionMaps.map((optMap: any) => ({
          value: optMap.option.name,
          label: optMap.option.name,
        }));
      }
    });
  });

  return options;
};

// Update the renderSectionName function
const renderSectionName = (name: string) => {
  if (!name) return "";

  const parts = name.split(/\b(Mahatria)\b/i);
  let result = "";

  parts.forEach((part) => {
    if (part.toLowerCase() === "mahatria") {
      result += `<span style="color: #d9251d">${part}</span>`;
    } else {
      result += part;
    }
  });

  return result;
};

const FormBuilderEdit: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sections, setSections] = useState<{
    sections: Array<{
      sectionId: number;
      sectionName: string;
      questions: any[];
      sectionDisplayOrder: number;
    }>;
  }>({ sections: [] });
  const [deletedQuestionIds, setDeletedQuestionIds] = useState<number[]>([]);
  const programId = location?.state?.programId;
  const [programQuestions, setProgramQuestions] = useState<
    ProgramQuestionMap[]
  >([]);
  const [columnLayout, setColumnLayout] = useState<"1" | "2">("2");

  const programQuestionMaps: ProgramQuestionMap[] =
    location.state?.programQuestionMaps || [];

  const fetchProgramQuestions = async () => {
    try {
      if (!programId) return;
      const response = await getCall(`${endPoints?.program}/${programId}`, undefined, PORTAL);

      if (
        response?.data?.statusCode === 200 &&
        response.data.data?.programQuestionMaps?.length > 0
      ) {
        const transformedQuestions = response.data.data.programQuestionMaps.map(
          (item: any) => ({
            id: item.id,
            question: {
              id: item.question.id,
              label: item.question.label,
              type: item.question.type,
              config: {
                ...getDefaultValidationConfig(item.question.type),
                ...item.question.config,
                isAdvancedValidation:
                  item.question.config?.isAdvancedValidation || false,
              },
              formSection: {
                id: item?.programQuestionFormSection?.id || 1,
                name:
                  item.programQuestionFormSection?.name ||
                  "General Information",
                description:
                  item.programQuestionFormSection?.description ||
                  "Please fill out the following information",
              },
              questionOptionMaps: item.question.questionOptionMaps || [],
              answerLocation: item.question.answerLocation || "",
              bindingKey: item.question.bindingKey || "",
            },
            displayOrder: item.displayOrder || 0,
            registrationLevel: item.registrationLevel || "program",
          }),
        );

        setProgramQuestions(transformedQuestions);
      } else {
        setProgramQuestions(programQuestionMaps);

        if (programQuestionMaps.length > 0) {
          const tempGroupedMap = programQuestionMaps.reduce(
            (acc: { [key: number]: any }, questionMap) => {
              const sectionId = questionMap.question.formSection.id;
              if (!acc[sectionId]) {
                acc[sectionId] = {
                  sectionId: sectionId,
                  sectionName: questionMap.question.formSection.name,
                  questions: [],
                  sectionDisplayOrder: 0,
                };
              }
              acc[sectionId].questions.push(questionMap);
              return acc;
            },
            {},
          );

          const sectionsArray = Object.values(tempGroupedMap).map(
            (section, index) => ({
              ...section,
              sectionDisplayOrder: index + 1,
            }),
          );

          sectionsArray.forEach((section) => {
            section.questions.sort((a, b) => a.displayOrder - b.displayOrder);
          });

          sectionsArray.sort(
            (a, b) => a.sectionDisplayOrder - b.sectionDisplayOrder,
          );

          const payload = {
            programId: programId,
            sections: sectionsArray.map((section, sectionIndex) => ({
              sectionId: section.sectionId,
              sectionName: section.sectionName,
              questions: section.questions.map((questionMap, index) => ({
                questionId: questionMap.question.id,
                displayOrder: index,
              })),
              sectionDisplayOrder: section.sectionDisplayOrder,
            })),
            registrationLevel: "program",
            programSessionId: 0,
          };

          try {
            const postResponse = await postCall(
              endPoints?.programQuestion,
              payload,
              PORTAL
            );
            if (postResponse?.data?.statusCode === 201) {
              fetchProgramQuestions();
            }
          } catch (error) {
            console.error("Error saving program questions:", error);
          }

          setSections({ sections: sectionsArray });
        }
      }
    } catch (error) {
      console.error("Error fetching program questions:", error);
      setProgramQuestions(programQuestionMaps);
    }
  };

  useEffect(() => {
    fetchProgramQuestions();
  }, [programId, programQuestionMaps]);

  useEffect(() => {
    if (!programQuestions.length) return;
    const tempGroupedMap = programQuestions.reduce(
      (acc: { [key: number]: any }, questionMap) => {
        const sectionId = questionMap.question.formSection.id;

        const transformedConfig = {
          ...getDefaultValidationConfig(questionMap.question.type),
          ...questionMap.question.config,
          dependsOn:
            questionMap.question.config?.dependsOn?.length > 0
              ? {
                  questionId:
                    questionMap.question.config.dependsOn[0].questionId,
                  // Collect all values from dependsOn array
                  value: questionMap.question.config.dependsOn.map(
                    (d) => d.value,
                  ),
                }
              : undefined,
        };

        const transformedQuestion = {
          ...questionMap,
          question: {
            ...questionMap.question,
            config: transformedConfig,
          },
        };

        if (!acc[sectionId]) {
          acc[sectionId] = {
            sectionId: sectionId,
            sectionName: questionMap.question.formSection.name,
            questions: [],
            sectionDisplayOrder: 0,
          };
        }

        acc[sectionId].questions.push(transformedQuestion);
        return acc;
      },
      {},
    );

    const sectionsArray = Object.values(tempGroupedMap).map(
      (section, index) => ({
        ...section,
        sectionDisplayOrder: index + 1,
      }),
    );

    sectionsArray.forEach((section) => {
      section.questions.sort((a, b) => a.displayOrder - b.displayOrder);
    });

    sectionsArray.sort((a, b) => a.sectionDisplayOrder - b.sectionDisplayOrder);

    setSections({ sections: sectionsArray });
  }, [programQuestions]);

  const addField = (sectionIndex: number) => {
    const currentSection = sections.sections[sectionIndex];

    const maxDisplayOrder =
      currentSection.questions.length > 0
        ? Math.max(...currentSection.questions.map((q) => q.displayOrder))
        : -1;

    const newDisplayOrder = maxDisplayOrder + 1;

    const newField = {
      id: Date.now(),
      isEdited: true,
      question: {
        label: "New Field",
        type: "text",
        config: getDefaultValidationConfig("text"),
        status: "published",
        formSection: {
          id: currentSection.sectionId,
          name: currentSection.sectionName,
          description: "",
        },
        questionOptionMaps: [],
      },
      displayOrder: newDisplayOrder,
    };

    const updatedSections = [...sections.sections];
    updatedSections[sectionIndex].questions.push(newField);
    setSections({ sections: updatedSections });
  };

  const updateField = (
    sectionIndex: number,
    questionIndex: number,
    updates: any,
  ) => {
    const updatedSections = [...sections.sections];
    const question = updatedSections[sectionIndex].questions[questionIndex];

    // Deep merge the updates
    const updatedQuestion = {
      ...question,
      isEdited: true,
      question: {
        ...question.question,
        ...updates,
        config: {
          ...question.question.config,
          ...(updates.config || {}),
        },
      },
    };

    updatedSections[sectionIndex].questions[questionIndex] = updatedQuestion;
    setSections({ sections: updatedSections });
  };

  const deleteField = (sectionIndex: number, questionIndex: number) => {
    const updatedSections = [...sections.sections];
    const questionToDelete =
      updatedSections[sectionIndex].questions[questionIndex];

    if (
      questionToDelete.question.id &&
      questionToDelete.question.id < 1000000000000
    ) {
      setDeletedQuestionIds((prev) => [...prev, questionToDelete.question.id]);
    }

    updatedSections[sectionIndex].questions.splice(questionIndex, 1);
    setSections({ sections: updatedSections });
  };

  const addSection = () => {
    const newSection = {
      sectionName: "New Section",
      questions: [],
      sectionDisplayOrder: sections.sections.length,
    };

    setSections({
      sections: [...sections.sections, newSection],
    });
  };

  const prepareApiPayload = () => {
    return {
      programId: location?.state?.programId || 0,
      sections: sections.sections
        .filter((section) => section.questions.some((q) => q.isEdited || !q.id))
        .map((section) => {
          const deletedQuestions = [
            ...deletedQuestionIds,
            ...section.questions
              .filter(
                (q) =>
                  q.isEdited && q.question.id && q.question.id < 1000000000000,
              )
              .map((q) => q.question.id),
          ];

          return {
            sectionName: section.sectionName,
            sectionId: section.sectionId,
            customQuestions: section.questions
              .filter((questionMap) => questionMap.isEdited || !questionMap.id)
              .map((questionMap) => ({
                question: {
                  label: questionMap.question.label,
                  type: questionMap.question.type,
                  section: section.sectionId,
                  config: {
                    ...questionMap.question.config,
                    dependsOn: questionMap.question.config.dependsOn
                      ? questionMap.question.config.dependsOn.value.map(
                          (value) => ({
                            questionId:
                              questionMap.question.config.dependsOn.questionId,
                            value: value,
                          }),
                        )
                      : undefined,
                  },
                  status: questionMap.question.status,
                  answerLocation: questionMap.question.answerLocation || "",
                  bindingKey: questionMap.question.bindingKey || "",
                },
                options: questionMap.question.questionOptionMaps.map((qom) => ({
                  name: qom.option.name,
                  type: qom.option.type,
                  category: null,
                })),
                displayOrder: questionMap.id
                  ? questionMap.displayOrder
                  : section.questions.length + 1,
              })),
            deletedQuestions,
            sectionDisplayOrder: section.sectionDisplayOrder,
          };
        }),
      registrationLevel: "program",
      programSessionId: 0,
    };
  };

  const handleContinue = async () => {
    try {
      const hasEditedQuestions = sections.sections.some((section) =>
        section.questions.some((q) => q.isEdited || !q.id),
      );

      if (hasEditedQuestions) {
        const payload = prepareApiPayload();

        const response = await putCall(
          `${endPoints?.programQuestion}`,
          payload,
          PORTAL
        );

        if (response?.data?.statusCode !== 200) {
          throw new Error("Failed to save form changes");
        }
      }

      navigate("/admin/preview-form", {
        state: {
          selectedForm: columnLayout,
          programQuestionMaps: programQuestionMaps,
          programName: location?.state?.programName || "New Program",
          bannerImage:
            location?.state?.bannerImage ||
            getProgramImage(location?.state?.programName),
          programId: programId,
          fromFormBuilder: false,
        },
      });
    } catch (error) {
      console.error("Error saving form before preview:", error);
    }
  };

  const handleSave = async () => {
    try {
      if (deletedQuestionIds.length > 0) {
        for (const questionId of deletedQuestionIds) {
          await deleteCall(`${endPoints?.programQuestion}/${questionId}`,undefined, PORTAL);
        }
      }

      const payload = prepareApiPayload();
    } catch (error) {
      console.error("Error saving form:", error);
    }
  };

  const handleBack = () => {
    navigate("/admin/add-program/hdb", {
      state: {
        programTypeId: location?.state?.programId,
        programName: location?.state?.programName,
        isEditMode: true,
      },
    });
  };

  function handleBannerUpload(event: ChangeEvent<HTMLInputElement>): void {
    throw new Error("Function not implemented.");
  }

  // Helper function to render field-specific validation controls
  const renderValidationControls = (
    field: any,
    sectionIndex: number,
    questionIndex: number,
  ) => {
    const fieldType = field.question.type;
    const config = field.question.config;

    // Only show validation controls for certain field types
    const validationSupportedTypes = [
      "text",
      "textarea",
      "number",
      "email",
      "file",
      "draganddrop",
      "year",
      "apicall",
      "date",
    ];

    if (!validationSupportedTypes.includes(fieldType)) {
      return null;
    }

    return (
      <>
        {/* Advanced Validation Toggle - shown for all supported types */}
        <div className="form-group full-width">
          <div className="field-title-group">
            <span className="required-text">Enable Advanced Validation</span>
            <label className="toggle-container">
              <input
                type="checkbox"
                checked={config.isAdvancedValidation || false}
                onChange={(e) =>
                  updateField(sectionIndex, questionIndex, {
                    config: {
                      ...config,
                      isAdvancedValidation: e.target.checked,
                    },
                  })
                }
                className="toggle-input"
              />
              <div className="toggle-switch"></div>
            </label>
          </div>
        </div>

        {/* Validation Fields - shown only when advanced validation is enabled */}
        {config.isAdvancedValidation && (
          <>
            {/* Text and Textarea specific validations */}
            {(fieldType === "text" || fieldType === "textarea") && (
              <>
                <div className="form-group">
                  <label className="form-label">Minimum Characters</label>
                  <input
                    type="number"
                    value={config.minCharacter || ""}
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          minCharacter: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter minimum characters"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Maximum Characters</label>
                  <input
                    type="number"
                    value={config.maxCharacters || ""}
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          maxCharacters: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter maximum characters"
                    min="0"
                  />
                </div>

                {fieldType === "text" && (
                  <div className="form-group full-width">
                    <label className="form-label">
                      Validation Pattern (Regex)
                    </label>
                    <input
                      type="text"
                      value={config.validationPattern || ""}
                      onChange={(e) =>
                        updateField(sectionIndex, questionIndex, {
                          config: {
                            ...config,
                            validationPattern: e.target.value || null,
                          },
                        })
                      }
                      className="form-input"
                      placeholder="e.g., ^[A-Za-z]+$ (letters only)"
                    />
                  </div>
                )}
              </>
            )}

            {/* Number specific validations */}
            {fieldType === "number" && (
              <>
                <div className="form-group">
                  <label className="form-label">Minimum Value</label>
                  <input
                    type="number"
                    value={config.minValue || ""}
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          minValue: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter minimum value"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Maximum Value</label>
                  <input
                    type="number"
                    value={config.maxValue || ""}
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          maxValue: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter maximum value"
                  />
                </div>

                <div className="form-group full-width">
                  <div className="field-title-group">
                    <span className="required-text">Allow Decimals</span>
                    <label className="toggle-container">
                      <input
                        type="checkbox"
                        checked={config.allowDecimals || false}
                        onChange={(e) =>
                          updateField(sectionIndex, questionIndex, {
                            config: {
                              ...config,
                              allowDecimals: e.target.checked,
                            },
                          })
                        }
                        className="toggle-input"
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Email specific validations */}
            {fieldType === "email" && (
              <>
                <div className="form-group full-width">
                  <label className="form-label">Email Pattern (Regex)</label>
                  <input
                    type="text"
                    value={
                      config.validationPattern ||
                      "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
                    }
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          validationPattern:
                            e.target.value || "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter email validation pattern"
                  />
                  {/* <div className="validation-hint">
                    Default pattern validates standard email format (e.g.,
                    user@domain.com)
                  </div> */}
                </div>
              </>
            )}

            {/* File and Drag & Drop specific validations */}
            {fieldType === "file" && (
              <>
                <div className="form-group">
                  <label className="form-label">Maximum File Size (MB)</label>
                  <input
                    type="number"
                    value={config.maxFileSize || ""}
                    onChange={(e) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          maxFileSize: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        },
                      })
                    }
                    className="form-input"
                    placeholder="Enter max file size"
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Allowed File Types</label>
                  <CustomDropDown
                    value={config.allowedFileTypes || ""}
                    onChange={(val) =>
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          allowedFileTypes: val.value,
                        },
                      })
                    }
                    options={[
                      { value: "image", label: "Images" },
                      { value: "video", label: "Videos" },
                      { value: "profile", label: "Profile" },
                    ]}
                    placeholder="Select file type category"
                    width={340}
                    className="form-select"
                  />
                  <div className="validation-hint">
                    {config.allowedFileTypes === "image" &&
                      "Accepts: .jpg, .jpeg, .png, .gif, .bmp, .webp, .svg"}
                    {config.allowedFileTypes === "video" &&
                      "Accepts: .mp4, .avi, .mov, .wmv, .flv, .webm, .mkv"}
                  </div>
                </div>

                {/* Custom file extensions input - only show when "Custom Extensions" is selected */}
                {config.allowedFileTypes === "custom" && (
                  <div className="form-group full-width">
                    <label className="form-label">Custom File Extensions</label>
                    <input
                      type="text"
                      value={config.customFileTypes || ""}
                      onChange={(e) =>
                        updateField(sectionIndex, questionIndex, {
                          config: {
                            ...config,
                            customFileTypes: e.target.value || null,
                          },
                        })
                      }
                      className="form-input"
                      placeholder="e.g., .jpg, .png, .pdf"
                    />
                    <div className="validation-hint">
                      Enter file extensions separated by commas (include the
                      dot)
                    </div>
                  </div>
                )}
              </>
            )}
            {fieldType === "draganddrop" && (
              <>
                {/* Add the new Mahatria Choice toggle */}
                <div className="form-group full-width">
                  <div className="field-title-group">
                    <span className="required-text">Allow Mahatria Choice</span>
                    <label className="toggle-container">
                      <input
                        type="checkbox"
                        checked={config.allowMahatriaChoice || false}
                        onChange={(e) =>
                          updateField(sectionIndex, questionIndex, {
                            config: {
                              ...config,
                              allowMahatriaChoice: e.target.checked,
                            },
                          })
                        }
                        className="toggle-input"
                      />
                      <div className="toggle-switch"></div>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Year specific validations */}
            {fieldType === "year" && (
              <>
                <div className="field-title-group">
                  <span className="required-text">Allow Past Years</span>
                  <label className="toggle-container">
                    <input
                      type="checkbox"
                      checked={config.allowPastYears || false}
                      onChange={(e) => {
                        const allowPast = e.target.checked;
                        updateField(sectionIndex, questionIndex, {
                          config: {
                            ...config,
                            minYear: allowPast ? null : "current",
                          },
                        });
                      }}
                      className="toggle-input"
                    />
                    <div className="toggle-switch"></div>
                  </label>
                </div>

                <div className="field-title-group">
                  <span className="required-text">Allow Future Years</span>
                  <label className="toggle-container">
                    <input
                      type="checkbox"
                      checked={config.allowFutureYears || false}
                      onChange={(e) => {
                        const allowFuture = e.target.checked;
                        updateField(sectionIndex, questionIndex, {
                          config: {
                            ...config,
                            maxYear: allowFuture ? null : "current",
                          },
                        });
                      }}
                      className="toggle-input"
                    />
                    <div className="toggle-switch"></div>
                  </label>
                </div>
              </>
            )}

            {/* API Call specific validations */}
            {fieldType === "apicall" && (
              <div className="form-group">
                <label className="form-label">Select API Endpoint</label>
                <CustomDropDown
                  value={config.endPoint || ""}
                  onChange={(val) =>
                    updateField(sectionIndex, questionIndex, {
                      config: {
                        ...config,
                        endPoint: val.value,
                      },
                    })
                  }
                  options={API_ENDPOINTS.map((endpoint) => ({
                    value: endpoint.value,
                    label: endpoint.label,
                  }))}
                  placeholder="Select API endpoint"
                  width={340}
                  className="form-select"
                />
              </div>
            )}

            {/* Date specific validations */}
            {fieldType === "date" && (
              <>
                <div className="form-group">
                  <label className="form-label">Date Validation Type</label>
                  <CustomDropDown
                    value={config.dateValidationType || "none"}
                    onChange={(selectedOption) => {
                      const newType =
                        typeof selectedOption === "object"
                          ? selectedOption.value
                          : selectedOption;
                      updateField(sectionIndex, questionIndex, {
                        config: {
                          ...config,
                          dateValidationType: newType,
                          startDate: null,
                          endDate: null,
                          pastValidation: {
                            enabled: false,
                            minValue: null,
                            maxValue: null,
                            unit: "days",
                          },
                          futureValidation: {
                            enabled: false,
                            minValue: null,
                            maxValue: null,
                            unit: "days",
                          },
                        },
                      });
                    }}
                    options={[
                      { value: "none", label: "No Restrictions" },
                      { value: "static", label: "Fixed Date Range" },
                      { value: "dynamic", label: "Relative Date Range" },
                    ]}
                    placeholder="Select validation type"
                    width={340}
                    className="form-select"
                  />
                </div>

                {config.dateValidationType === "static" && (
                  <div className="form-group">
                    <div className={styles.formLaunchDateContainer}>
                      <div className={styles.formLaunchDate}>
                        <label className="form-label">Start Date</label>
                        <CustomDatePicker
                          value={config.startDate || ""}
                          onChange={(date) =>
                            updateField(sectionIndex, questionIndex, {
                              config: { ...config, startDate: date },
                            })
                          }
                          futureDate={false}
                          startDate={new Date()}
                          borderRight={true}
                          errorExist={false}
                          dataTestId="date-start"
                        />
                      </div>

                      <div className={styles.formLaunchDate}>
                        <label className="form-label">End Date</label>
                        <CustomDatePicker
                          value={config.endDate || ""}
                          onChange={(date) =>
                            updateField(sectionIndex, questionIndex, {
                              config: { ...config, endDate: date },
                            })
                          }
                          futureDate={true}
                          startDate={
                            config.startDate
                              ? new Date(config.startDate)
                              : new Date()
                          }
                          borderRight={true}
                          errorExist={false}
                          dataTestId="date-end"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {config.dateValidationType === "dynamic" && (
                  <>
                    {/* Past Date Validation */}
                    <div className="form-group full-width">
                      <div className="field-title-group">
                        <span className="required-text">Allow Past Dates</span>
                        <label className="toggle-container">
                          <input
                            type="checkbox"
                            checked={config.pastValidation?.enabled || false}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  pastValidation: {
                                    enabled: e.target.checked,
                                    minValue: null,
                                    maxValue: null,
                                    unit: "days",
                                  },
                                },
                              })
                            }
                            className="toggle-input"
                          />
                          <div className="toggle-switch"></div>
                        </label>
                      </div>
                    </div>

                    {config.pastValidation?.enabled && (
                      <div className={styles.formLaunchDateContainer}>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Min Value</label>
                          <input
                            type="number"
                            value={config.pastValidation?.minValue || ""}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  pastValidation: {
                                    ...config.pastValidation,
                                    minValue: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  },
                                },
                              })
                            }
                            className="form-input"
                            placeholder="Min"
                          />
                        </div>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Max Value</label>
                          <input
                            type="number"
                            value={config.pastValidation?.maxValue || ""}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  pastValidation: {
                                    ...config.pastValidation,
                                    maxValue: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  },
                                },
                              })
                            }
                            className="form-input"
                            placeholder="Max"
                          />
                        </div>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Unit</label>
                          <CustomDropDown
                            value={config.pastValidation?.unit || "days"}
                            onChange={(selectedOption) => {
                              // Ensure we get the value correctly whether it's an object or direct value
                              const unitValue =
                                typeof selectedOption === "object"
                                  ? selectedOption.value
                                  : selectedOption;

                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  pastValidation: {
                                    ...config.pastValidation,
                                    enabled: true, // Keep enabled state
                                    minValue:
                                      config.pastValidation?.minValue || null,
                                    maxValue:
                                      config.pastValidation?.maxValue || null,
                                    unit: unitValue,
                                  },
                                },
                              });
                            }}
                            options={[
                              { value: "days", label: "Days" },
                              { value: "months", label: "Months" },
                              { value: "years", label: "Years" },
                            ]}
                            placeholder="Select unit"
                            className="form-select"
                          />
                        </div>
                      </div>
                    )}

                    {/* Future Date Validation - Same structure */}
                    <div className="form-group full-width">
                      <div className="field-title-group">
                        <span className="required-text">
                          Allow Future Dates
                        </span>
                        <label className="toggle-container">
                          <input
                            type="checkbox"
                            checked={config.futureValidation?.enabled || false}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  futureValidation: {
                                    enabled: e.target.checked,
                                    minValue: null,
                                    maxValue: null,
                                    unit: "days",
                                  },
                                },
                              })
                            }
                            className="toggle-input"
                          />
                          <div className="toggle-switch"></div>
                        </label>
                      </div>
                    </div>

                    {config.futureValidation?.enabled && (
                      <div className={styles.formLaunchDateContainer}>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Min Value</label>
                          <input
                            type="number"
                            value={config.futureValidation?.minValue || ""}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  futureValidation: {
                                    ...config.futureValidation,
                                    minValue: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  },
                                },
                              })
                            }
                            className="form-input"
                            placeholder="Min"
                          />
                        </div>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Max Value</label>
                          <input
                            type="number"
                            value={config.futureValidation?.maxValue || ""}
                            onChange={(e) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  futureValidation: {
                                    ...config.futureValidation,
                                    maxValue: e.target.value
                                      ? parseInt(e.target.value)
                                      : null,
                                  },
                                },
                              })
                            }
                            className="form-input"
                            placeholder="Max"
                          />
                        </div>
                        <div className={styles.formLaunchDate}>
                          <label className="form-label">Unit</label>
                          <CustomDropDown
                            value={config.futureValidation?.unit || "days"}
                            onChange={(val) =>
                              updateField(sectionIndex, questionIndex, {
                                config: {
                                  ...config,
                                  futureValidation: {
                                    ...config.futureValidation,
                                    unit: val.value,
                                  },
                                },
                              })
                            }
                            options={[
                              { value: "days", label: "Days" },
                              { value: "months", label: "Months" },
                              { value: "years", label: "Years" },
                            ]}
                            placeholder="Select unit"
                            className="form-select"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <div className={styles.programHeader}>
        <div className={styles.programHeaderText}>
          <ArrowLeft
            size={24}
            onClick={handleBack}
            className={styles.arrowIcon}
          />
          Configure registration form
        </div>
      </div>
      <div>
        <Stepper currentStep={2} />
      </div>
      <style>{`
        .form-builder {
          display: flex;
          flex-direction: column;
          max-width: 100vw;
          margin: 0 auto;
          align-items: center;
          padding: 24px;
          background-color:#FFFFF;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding-bottom: 75px;
          gap:40px;
        }
        .program-banner {
          width: 71%;
          height: 375px;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
        }
        .program-banner img {
          width: 100%;
          height: 100%;
        }
        .buttonText {
  color: #ffffff;
  font-size: 16px;
  font-weight: 500; 
}

.buttonContainer {
  font-size: 16px;
  height: 40px;
  margin-right: 32px;
      position: absolute;
    right: 0px;
    bottom: 20px;
}

        .section {
          width:71%;
          margin-bottom: 32px;
          background-color: white;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px;
          gap:20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .section-title-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }
          

        .section-title-input {
          font-size: 18px;
          font-weight: 600;
          border: none;
          background: transparent;
          outline: none;
          width: auto;
          padding: 0;
          margin: 0;
        }

        .section-title-input:focus {
          background-color: #f9fafb;
        }

        .section-content {
          padding: 24px;
        }

        .field {
          border:8px solid #F9FAFF;
          border-radius: 8px;
          padding: 24px;
          border-width:8px;
          background-color:#FFFFF;
          margin-bottom: 24px;
        }

        .field:last-child {
          margin-bottom: 0;
        }

        .field-header {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .field-title-group {
          display: flex;
          width:100%;
          align-items: center;
          gap: 12px;
        }

        .field-label-input {
          font-weight: 500;
          width:100%;
          background: transparent;
          border: none;
          outline: none;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .field-label-input:focus {
          background-color: white;
        }

        .required-indicator {
          color: #ef4444;
        }

        .required-text {
          font-size: 14px;
          color: #4D5154;
        }

        .toggle-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
        }

        .toggle-input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .toggle-switch {
          width: 32px;
          height: 16px;
          background-color: #d1d5db;
          border-radius: 12px;
          position: relative;
          transition: background-color 0.2s;
        }

        .toggle-switch::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 12px;
          height: 12px;
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 50%;
          transition: transform 0.2s;
        }

        .toggle-input:checked + .toggle-switch {
          background-color: #1859B4;
          width: 32px;
          height: 16px;
        }

        .toggle-input:checked + .toggle-switch::after {
          transform: translateX(10px);
          border-color: white;
          width: 12px;
          height: 12px;
          margin-left: 6px;
        }

        .delete-btn {
          padding: 8px;
          color: #9ca3af;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .delete-btn:hover {
          color: #ef4444;
          background-color: #fef2f2;
        }

        .field-config {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          background-color: white;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          resize: none;
          height: 80px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .grip-icon {
          color: #9ca3af;
          font-size: 16px;
          font-weight: bold;
        }

        .actions-container {
          margin-top: 24px;
          text-align: right;
          padding: 0 24px;
        }
        
        .save-btn {
          padding: 12px 24px;
          background-color: #10B981;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .save-btn:hover {
          background-color: #059669;
        }
         
        .section-title-editable:hover .edit-icon,
        .section-title-input:focus + .edit-icon {
          opacity: 1 !important;
          pointer-events: auto !important;
        }

        .section-title-editable .edit-icon {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
        }

        .validation-hint {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
          font-style: italic;
        }

        .validation-error {
          font-size: 12px;
          color: #ef4444;
          margin-top: 4px;
        }
      `}</style>

      <div className="form-builder">
        <div className="program-banner">
          <img
            src={location?.state?.bannerImage}
            alt="Program Banner"
            className={styles.programBannerImage}
          />
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            id="banner-upload-input"
            onChange={handleBannerUpload}
          />
          <Button
            type="button"
            buttonClassName={styles.buttonContainer}
            buttonTextClassName={styles.buttonText}
            datatestid="add-program-save-button"
            datatestidText="add-program-save"
            onClick={() => {
              const input = document.getElementById(
                "banner-upload-input",
              ) as HTMLInputElement;
              if (input) input.click();
            }}
            lessPadding={true}
          >
            change banner
          </Button>
        </div>
        <div className={styles.layoutToggle}>
          <span className={styles.heading}>Form layout:</span>
          <div className={styles.layoutButtons}>
            <button
              className={`${styles.layoutButton} ${
                columnLayout === "1" ? styles.active : ""
              }`}
              onClick={() => setColumnLayout("1")}
            >
              1 Column
            </button>
            <button
              className={`${styles.layoutButton} ${
                columnLayout === "2" ? styles.active : ""
              }`}
              onClick={() => setColumnLayout("2")}
            >
              2 Column
            </button>
          </div>
        </div>
        {sections.sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="section">
            <div className="section-header">
              <div
                className="section-title-editable"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0px",
                  position: "relative",
                  minWidth: 0,
                }}
              >
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => {
                    const text = e.currentTarget.textContent || "";
                    const updatedSections = [...sections.sections];
                    updatedSections[sectionIndex].sectionName = text;
                    setSections({ sections: updatedSections });
                  }}
                  dangerouslySetInnerHTML={{
                    __html: renderSectionName(section.sectionName),
                  }}
                  className="section-title-input"
                  style={{
                    width: `${Math.max(section.sectionName.length * 12, 60)}px`,
                    minWidth: "50px",
                  }}
                />
                <div className="edit-icon">
                  <img src={editbutton} alt="edit" />
                </div>
              </div>

              <Button
                buttonClassName={styles.programsDashboardAddButton}
                onClick={() => addField(sectionIndex)}
                datatestid="create-infinipath"
                datatestidText="create-infinipath-text"
              >
                add field
              </Button>
            </div>

            <div className="section-content">
              {section.questions.map((field, questionIndex) => (
                <div key={field.id} className="field">
                  <div className="field-header">
                    <div className="field-title-group">
                      <div className="field-title-group">
                        <input
                          type="text"
                          value={field.question.label}
                          onChange={(e) =>
                            updateField(sectionIndex, questionIndex, {
                              label: e.target.value,
                            })
                          }
                          className="field-label-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="field-config">
                    <div className="form-group">
                      <label className="form-label">Label Name*</label>
                      <input
                        type="text"
                        value={field.question.label}
                        onChange={(e) =>
                          updateField(sectionIndex, questionIndex, {
                            label: e.target.value,
                          })
                        }
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Field Type*</label>
                      <CustomDropDown
                        value={field.question.type}
                        onChange={(selectedOption) => {
                          // Ensure we're getting the value correctly from the selected option
                          const newType =
                            selectedOption?.value || selectedOption;

                          updateField(sectionIndex, questionIndex, {
                            type: newType,
                            questionOptionMaps: [], // Clear options when type changes
                          });
                        }}
                        options={fieldTypes.map((type) => ({
                          value: type.value,
                          label: type.label,
                        }))}
                        placeholder="Select field type"
                        width={340}
                        className="form-select"
                        menuwidth={80}
                        menuHeight={200}
                        // Add key prop to force re-render when value changes
                        key={`${sectionIndex}-${questionIndex}-${field.question.type}`}
                      />
                    </div>
                    {/* Placeholder field for applicable types */}
                    {![
                      "password",
                      "radio",
                      "checkbox",
                      "date",
                      "file",
                      "boolean",
                    ].includes(field.question.type) && (
                      <div className="form-group full-width">
                        <label className="form-label">Placeholder</label>
                        <input
                          type="text"
                          value={field.question.config.placeholder || ""}
                          onChange={(e) =>
                            updateField(sectionIndex, questionIndex, {
                              config: {
                                ...field.question.config,
                                placeholder: e.target.value,
                              },
                            })
                          }
                          className="form-input"
                          placeholder="Enter placeholder text"
                        />
                      </div>
                    )}

                    {/* Options Manager for radio, checkbox, select */}
                    {(field.question.type === "radio" ||
                      field.question.type === "checkbox" ||
                      field.question.type === "select") && (
                      <div className="form-group full-width">
                        <label className="form-label">
                          Options Manager (comma separated)
                        </label>
                        <textarea
                          value={field.question.questionOptionMaps
                            .map((optionMap) => optionMap.option.name)
                            .join(",")}
                          onChange={(e) => {
                            const inputValue = e.target.value;

                            // Simple split - let user type freely
                            const optionNames = inputValue.split(",");

                            const options = optionNames.map((name, index) => ({
                              id: index,
                              name: name, // Don't trim here - let user control spacing
                              type: "string",
                              status: "published",
                            }));

                            const questionOptionMaps = options.map(
                              (option, index) => ({
                                id: index,
                                option,
                              }),
                            );

                            updateField(sectionIndex, questionIndex, {
                              questionOptionMaps,
                            });
                          }}
                          className="form-textarea"
                          placeholder="Enter options (e.g. Option1,Option2,Option3)"
                        />
                        <div className="validation-hint">
                          Enter options separated by commas. For example:
                          Option1,Option2,Option3
                        </div>
                      </div>
                    )}

                    {/* Depends On functionality */}
                    {(() => {
                      const availableRadioQuestions =
                        getAvailableRadioQuestions(
                          sections.sections,
                          questionIndex,
                          sectionIndex,
                        );

                      return availableRadioQuestions.length > 0 ? (
                        <>
                          <div className="form-group">
                            <label className="form-label">Depends On</label>
                            <CustomDropDown
                              value={
                                field.question.config.dependsOn?.questionId ||
                                ""
                              }
                              onChange={(val) => {
                                const selectedValue =
                                  typeof val === "object" ? val.value : val;
                                updateField(sectionIndex, questionIndex, {
                                  config: {
                                    ...field.question.config,
                                    dependsOn: selectedValue
                                      ? {
                                          questionId: selectedValue,
                                          value: [],
                                        }
                                      : undefined,
                                  },
                                });
                              }}
                              options={availableRadioQuestions.map((q) => ({
                                value: q.id,
                                label: q.label,
                              }))}
                              placeholder="Select question"
                              width={340}
                              className="form-select"
                            />
                            {/* <div className="validation-hint">
                              This field will only show when the selected
                              question has specific values
                            </div> */}
                          </div>

                          {field.question.config.dependsOn?.questionId && (
                            <div className="form-group">
                              <label className="form-label">
                                Depends On Value
                              </label>
                              <Select
                                isMulti
                                value={field.question.config.dependsOn.value.map(
                                  (val) => ({
                                    value: val,
                                    label: val,
                                  }),
                                )}
                                onChange={(selectedOptions) => {
                                  const values = selectedOptions
                                    ? selectedOptions.map(
                                        (option) => option.value,
                                      )
                                    : [];

                                  updateField(sectionIndex, questionIndex, {
                                    config: {
                                      ...field.question.config,
                                      dependsOn: {
                                        questionId:
                                          field.question.config.dependsOn
                                            .questionId,
                                        value: values,
                                      },
                                    },
                                  });
                                }}
                                options={getRadioOptionsForQuestion(
                                  sections.sections,
                                  field.question.config.dependsOn.questionId,
                                )}
                                classNamePrefix="dependsOnSelect"
                                placeholder="Select values"
                              />
                            </div>
                          )}
                        </>
                      ) : null;
                    })()}

                    {/* Field-specific validation controls */}
                    {renderValidationControls(
                      field,
                      sectionIndex,
                      questionIndex,
                    )}
                  </div>
                  <hr className={styles.sectionDivider} />
                  <div className={styles.sectionFooter}>
                    <div className={styles.sectionFooterLeft}>
                      <span className="required-text">mark as required</span>
                      <label className="toggle-container">
                        <input
                          type="checkbox"
                          checked={field.question.config.isRequired}
                          onChange={(e) =>
                            updateField(sectionIndex, questionIndex, {
                              config: {
                                ...field.question.config,
                                isRequired: e.target.checked,
                              },
                            })
                          }
                          className="toggle-input"
                        />
                        <div className="toggle-switch"></div>
                      </label>
                    </div>
                    <div className={styles.sectionFooterDivider}></div>
                    <button
                      onClick={() => deleteField(sectionIndex, questionIndex)}
                      className="delete-btn"
                    >
                      <img
                        src={deleteIcon}
                        alt="Delete"
                        className={styles.deleteIcon}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className={styles.addSectionContainer}>
          <button onClick={addSection} className={styles.addSectionBtn}>
            <span className={styles.addSectionPlus}>+</span>
            <span className={styles.addSectionText}>add section</span>
          </button>
        </div>
      </div>
      <div className={styles.divWithButtons}>
        <div className={styles.formRowButton}>
          <Button
            onClick={() => navigate("/admin/action-cards")}
            buttonClassName={styles.buttonContainerSecondary}
            buttonTextClassName={styles.buttonTextSecondary}
          >
            cancel
          </Button>

          <Button
            onClick={handleContinue}
            buttonClassName={styles.buttonContainerPrimary}
            buttonTextClassName={styles.buttonTextPrimary}
          >
            continue
          </Button>
        </div>
      </div>
    </>
  );
};

export default FormBuilderEdit;
